import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import { globalStyles } from "../../../styles/GlobalStyles";
import formatDateTime from "../../../helpers/formatDateTime";
import { useTranslation } from "react-i18next";
import { CASE_TIMELINE_ITEM } from "./CaseSummary";
import { MILESTONE_TIMELINE } from "../types/CommonTypes";
import { toggleEditMilestoneTimeDrawer } from "./EditMilestoneTimeDrawer";
import { toggleRevisedMilestoneTimeModal } from "./RevisedMilestoneTimeDrawer";
import getMilestoneDisplayName from "../helpers/getMilestoneDisplayName";
import { DATE_TYPE } from "../../../utils/Types";

type TIMELINE_FLAG_TYPE = "minGap" | "sequence" | "amPm";

const getFlagType = ({
  isMinimumGapViolation,
  isEarlierThanPrior,
  isAmPmCheck,
}: {
  isMinimumGapViolation?: boolean;
  isEarlierThanPrior?: boolean;
  isAmPmCheck?: boolean;
}): TIMELINE_FLAG_TYPE | null => {
  // Keep display priority aligned with case-summary validation priority.
  if (isMinimumGapViolation) {
    return "minGap";
  }
  if (isEarlierThanPrior) {
    return "sequence";
  }
  if (isAmPmCheck) {
    return "amPm";
  }
  return null;
};

const BADGE_LABEL_KEY: Record<TIMELINE_FLAG_TYPE, string> = {
  minGap: "Minimum_Gap_Required",
  sequence: "Sequence_Check",
  amPm: "Check_AM_PM",
};

const ALERT_LABEL_KEY: Record<TIMELINE_FLAG_TYPE, string> = {
  minGap: "Minimum_1min_Gap_Required_From",
  sequence: "Out_of_sequence_with",
  amPm: "Earlier_than_prior_milestone",
};
interface MilestoneTimelineProps {
  item: MILESTONE_TIMELINE;
  hideLine?: boolean;
  isNotLast?: boolean;
  isEditMode?: boolean;
  timelineItemIndex: number;
  onRevisionPress?: (item: CASE_TIMELINE_ITEM) => void;
  priorMilestone?: MILESTONE_TIMELINE;
}

interface ValidationBadgeProps {
  flagType: TIMELINE_FLAG_TYPE;
  isHardBlock?: boolean;
  iconSize?: number;
}

const ValidationBadge = ({
  flagType,
  isHardBlock,
  iconSize = 14,
}: ValidationBadgeProps) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={[
        styles.flagBadge,
        isHardBlock ? styles.hardBlockBadge : styles.softBlockBadge,
      ]}
    >
      <WarningAmberRoundedIcon
        sx={{
          fontSize: iconSize,
          color: "#FFFFFF",
        }}
      />

      <Typography sx={styles.flagBadgeText}>
        {t(BADGE_LABEL_KEY[flagType])}
      </Typography>
    </Box>
  );
};

interface ValidationAlertProps {
  flagType: TIMELINE_FLAG_TYPE;
  isHardBlock?: boolean;
  milestoneName?: string;
  milestoneTime?: DATE_TYPE;
  priorMilestoneName?: string;
  priorMilestoneTime?: DATE_TYPE;
  minimumGapMinutes?: number;
  additional?: boolean;
}

const ValidationAlert = ({
  flagType,
  isHardBlock,
  milestoneName,
  milestoneTime,
  priorMilestoneName,
  priorMilestoneTime,
  minimumGapMinutes,
  additional = false,
}: ValidationAlertProps) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={[
        additional ? styles.additionalAlertBox : styles.alertBox,
        isHardBlock ? styles.hardAlertBox : styles.softAlertBox,
      ]}
    >
      <Typography
        sx={[
          styles.alertText,
          isHardBlock ? styles.hardAlertText : styles.softAlertText,
        ]}
      >
        {t(ALERT_LABEL_KEY[flagType], {
          milestone: getMilestoneDisplayName(milestoneName),
          time: formatDateTime(milestoneTime, true, "hh:mm:ss aaa"),
          prev_milestone: getMilestoneDisplayName(priorMilestoneName),
          prev_time: formatDateTime(priorMilestoneTime, true, "hh:mm:ss aaa"),
          minimum_gap: minimumGapMinutes ?? 1,
        })}
      </Typography>
    </Box>
  );
};

interface RevisionBadgeProps {
  revisionCount: number;
  onClick: () => void;
}

const RevisionBadge = ({ revisionCount, onClick }: RevisionBadgeProps) => {
  const { t } = useTranslation();

  const translationKey =
    revisionCount === 1 ? "revision_logged_count" : "revisions_logged_count";

  return (
    <Box onClick={onClick} sx={styles.revisionBadge}>
      <Typography sx={styles.revisionText}>
        {t(translationKey, {
          count: revisionCount,
        })}
      </Typography>
    </Box>
  );
};

interface SkipReasonProps {
  skipReason?: string;
  skipReasonNote?: string;
}

const SkipReason = ({ skipReason, skipReasonNote }: SkipReasonProps) => {
  const { t } = useTranslation();

  if (!skipReason && !skipReasonNote) {
    return null;
  }

  const reasonText = skipReasonNote?.length ? skipReasonNote : skipReason;
  return (
    <Box sx={styles.reasonBox}>
      <Typography sx={styles.reasonLabel}>{t("Reason")}</Typography>

      <Typography sx={styles.reasonText}>{reasonText}</Typography>
    </Box>
  );
};

interface AdditionalMilestoneProps {
  item: MILESTONE_TIMELINE;
  isNotLast: boolean;
  isEditMode: boolean;
  showFlag: boolean;
  flagType: TIMELINE_FLAG_TYPE | null;
  revisionCount: number;
  onTimestampClick: () => void;
  onRevisionClick: () => void;
}

const AdditionalMilestone = ({
  item,
  isNotLast,
  isEditMode,
  showFlag,
  flagType,
  revisionCount,
  onTimestampClick,
  onRevisionClick,
}: AdditionalMilestoneProps) => {
  const { t } = useTranslation();

  const {
    title,
    timestamp,
    loggedBy,
    isHardBlock,
    priorMilestoneName,
    priorMilestoneTime,
    minimumGapMinutes,
    skipped,
    skipReason,
    skipReasonNote,
  } = item;

  return (
    <Box
      sx={[
        styles.additionalMilestoneItem,
        !isNotLast && styles.additionalMilestoneItemLast,
      ]}
    >
      <Box sx={styles.additionalTitleRow}>
        <Typography sx={styles.additionalMilestoneTitle}>
          {getMilestoneDisplayName(title)}
        </Typography>
        {skipped && (
          <Typography sx={styles.skippedText}>
            ({t("CaseBoard_Skipped")})
          </Typography>
        )}
     
        {showFlag && flagType && (
          <ValidationBadge
            flagType={flagType}
            isHardBlock={isHardBlock}
            iconSize={11}
          />
        )}
      </Box>
      <Box sx={styles.TimeRow}>
        <Typography sx={[styles.dateStyle]}>
          {timestamp ? formatDateTime(timestamp, true, "dd,MMM") : ""}
        </Typography>
        <Typography
          onClick={onTimestampClick}
          sx={[
            styles.additionalTimestamp,
            isEditMode && styles.additionalEditableTimestamp,
          ]}
        >
          {timestamp
            ? formatDateTime(timestamp, true, "hh:mm:ss aaa")
            : "--:--"}
        </Typography>
      </Box>

      <Typography sx={styles.additionalLoggedBy}>
        {loggedBy ? `Logged by ${loggedBy}` : t("not_logged")}
      </Typography>

      {showFlag && flagType && (
        <ValidationAlert
          additional
          flagType={flagType}
          isHardBlock={isHardBlock}
          milestoneName={title}
          milestoneTime={timestamp}
          priorMilestoneName={priorMilestoneName}
          priorMilestoneTime={priorMilestoneTime}
          minimumGapMinutes={minimumGapMinutes}
        />
      )}

      {revisionCount > 0 && (
        <RevisionBadge
          revisionCount={revisionCount}
          onClick={onRevisionClick}
        />
      )}

      {skipped && (
        <SkipReason skipReason={skipReason} skipReasonNote={skipReasonNote} />
      )}
    </Box>
  );
};

interface StandardMilestoneProps {
  item: MILESTONE_TIMELINE;
  isNotLast: boolean;
  isEditMode: boolean;
  showFlag: boolean;
  flagType: TIMELINE_FLAG_TYPE | null;
  revisionCount: number;
  onTimestampClick: () => void;
  onRevisionClick: () => void;
}

const StandardMilestone = ({
  item,
  isNotLast,
  isEditMode,
  showFlag,
  flagType,
  revisionCount,
  onTimestampClick,
  onRevisionClick,
}: StandardMilestoneProps) => {
  const { t } = useTranslation();

  const {
    title,
    timestamp,
    loggedBy,
    isHardBlock,
    priorMilestoneName,
    priorMilestoneTime,
    minimumGapMinutes,
    skipped,
    skipReason,
    skipReasonNote,
  } = item;

  return (
    <Box sx={globalStyles.row}>
      <Box sx={styles.timelineColumn}>
        <RadioButtonCheckedIcon
          sx={{
            fontSize: 22,
            color: "custom.background.activity",
          }}
        />

        {isNotLast && <Box sx={styles.line} />}
      </Box>

      <Box
        sx={[styles.milestoneCard, isNotLast && styles.milestoneCardSpacing]}
      >
        <Box sx={styles.headerRow}>
          <Typography sx={styles.milestoneTitle}>
            {getMilestoneDisplayName(title)}
          </Typography>
          <Box sx={styles.TimeRow}>
            <Typography sx={[styles.dateStyle]}>
              {timestamp ? formatDateTime(timestamp, true, "dd,MMM") : ""}
            </Typography>
            <Typography
              onClick={onTimestampClick}
              sx={[styles.timeTextBox, isEditMode && styles.editModeTextBox]}
            >
              {timestamp
                ? formatDateTime(timestamp, true, "hh:mm:ss aaa")
                : "--:--"}
            </Typography>
          </Box>
            {skipped && (
            <Typography sx={styles.skippedText}>
              ({t("CaseBoard_Skipped")})
            </Typography>
          )}

          {showFlag && flagType && (
            <ValidationBadge flagType={flagType} isHardBlock={isHardBlock} />
          )}
        </Box>

        <Typography sx={styles.loggedByText}>
          {timestamp
            ? formatDateTime(timestamp, true, "hh:mm:ss aaa")
            : "--:--"}

          {" • "}

          {loggedBy ? `Logged by ${loggedBy}` : t("not_logged")}
        </Typography>

        {showFlag && flagType && (
          <ValidationAlert
            flagType={flagType}
            isHardBlock={isHardBlock}
            milestoneName={title}
            milestoneTime={timestamp}
            priorMilestoneName={priorMilestoneName}
            priorMilestoneTime={priorMilestoneTime}
            minimumGapMinutes={minimumGapMinutes}
          />
        )}

        {revisionCount > 0 && (
          <RevisionBadge
            revisionCount={revisionCount}
            onClick={onRevisionClick}
          />
        )}

        {skipped && (
          <SkipReason skipReason={skipReason} skipReasonNote={skipReasonNote} />
        )}
      </Box>
    </Box>
  );
};

const MilestoneTimeline = ({
  item,
  hideLine = false,
  isNotLast = false,
  isEditMode = false,
  timelineItemIndex,
  onRevisionPress,
  priorMilestone,
}: MilestoneTimelineProps) => {
  const {
    caseId,
    otId,
    title,
    timestamp,
    revision = [],
    milestoneId,
    isOptionalMilestone,
    milestoneKey,
    isEarlierThanPrior,
    isAmPmCheck,
    isMinimumGapViolation,
    priorMilestoneName,
    priorMilestoneTime,
  } = item;

  const flagType = getFlagType({
    isMinimumGapViolation,
    isEarlierThanPrior,
    isAmPmCheck,
  });

  const showFlag = Boolean(
    flagType && priorMilestoneName && priorMilestoneTime
  );

  const revisionCount = useMemo(
    () => Math.max((revision?.length ?? 0) - 1, 0),
    [revision?.length]
  );

  const handleTimestampClick = () => {
    if (!isEditMode) {
      return;
    }

    toggleEditMilestoneTimeDrawer({
      caseId: caseId ? parseInt(caseId) : undefined,
      otId,
      isOptionalMilestone,
      milestoneId,
      milestoneUUID: item.milestoneUUID,
      milestoneName: title,
      timelineItemIndex,
      milestoneKey,
      revision,
      timestamp,
      priorMilestoneName: priorMilestoneName ?? priorMilestone?.title,
      priorMilestoneTime: priorMilestoneTime ?? priorMilestone?.timestamp,
    });
  };

  const handleRevisionClick = () => {
    if (onRevisionPress) {
      onRevisionPress(item);
      return;
    }

    toggleRevisedMilestoneTimeModal({
      milestoneName: title,
      revision,
    });
  };

  if (hideLine) {
    return (
      <AdditionalMilestone
        item={item}
        isNotLast={isNotLast}
        isEditMode={isEditMode}
        showFlag={showFlag}
        flagType={flagType}
        revisionCount={revisionCount}
        onTimestampClick={handleTimestampClick}
        onRevisionClick={handleRevisionClick}
      />
    );
  }

  return (
    <StandardMilestone
      item={item}
      isNotLast={isNotLast}
      isEditMode={isEditMode}
      showFlag={showFlag}
      flagType={flagType}
      revisionCount={revisionCount}
      onTimestampClick={handleTimestampClick}
      onRevisionClick={handleRevisionClick}
    />
  );
};
const styles = {
  timelineColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    mr: "12px",
  },

  line: {
    width: "4px",
    flex: 1,
    backgroundColor: "custom.background.activity",
  },

  milestoneCard: {
    flex: 1,
    minWidth: 0,
    minHeight: "88px",
    mt: "-3px",
    backgroundColor: "custom.background.primary",
    border: "1px solid #E5E7EB",
    borderRadius: "8px",
    px: "12px",
    py: "10px",
    boxSizing: "border-box",
  },

  milestoneCardSpacing: {
    mb: "16px",
  },

  compactMilestoneItem: {
    width: "100%",
    boxSizing: "border-box",
    py: "12px",
    borderBottom: "1px solid #E5E7EB",
  },

  headerRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    columnGap: "6px",
    rowGap: "4px",
  },
  TimeRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    columnGap: "2px",
  },

  milestoneTitle: {
    fontSize: "16px",
    lineHeight: "22px",
    fontWeight: 700,
    color: "custom.foreground.primary",
  },

  timeTextBox: {
    border: "1px solid transparent",
    borderRadius: "4px",
    px: "4px",
    py: "1px",
    width: "fit-content",
    fontSize: "16px",
    lineHeight: "22px",
    fontWeight: 700,
    color: "custom.foreground.primary",
  },

  editModeTextBox: {
    borderColor: "#79747E",
    cursor: "pointer",

    "&:hover": {
      backgroundColor: "#F9FAFB",
    },
  },

  loggedByText: {
    mt: "3px",
    fontSize: "12px",
    lineHeight: "16px",
    color: "custom.foreground.secondary",
  },

  flagBadge: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "3px",
    px: "5px",
    py: "2px",
    borderRadius: "3px",
    width: "fit-content",
  },

  hardBlockBadge: {
    backgroundColor: "#E00000",
  },

  softBlockBadge: {
    backgroundColor: "#FF8801",
  },

  flagBadgeText: {
    fontSize: "10px",
    lineHeight: "14px",
    fontWeight: 600,
    color: "#FFFFFF",
  },

  alertBox: {
    width: "fit-content",
    maxWidth: "100%",
    boxSizing: "border-box",
    mt: "7px",
    border: "1px solid",
    borderRadius: "4px",
    px: "8px",
    py: "6px",
  },

  hardAlertBox: {
    borderColor: "#FFC9C9",
    backgroundColor: "#FEF2F2",
  },

  softAlertBox: {
    borderColor: "#FED7AA",
    backgroundColor: "#FFF7ED",
  },

  alertText: {
    fontSize: "11px",
    lineHeight: "16px",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },

  hardAlertText: {
    color: "#E00000",
  },

  softAlertText: {
    color: "#C2410C",
  },

  revisionBadge: {
    display: "flex",
    alignItems: "center",
    width: "fit-content",
    mt: "6px",
    border: "1px solid #84ADFF",
    borderRadius: "4px",
    px: "6px",
    py: "3px",
    backgroundColor: "#FFFFFF",
    cursor: "pointer",
  },

  revisionText: {
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 500,
    color: "#155EEF",
  },
  additionalMilestoneItem: {
    width: "100%",
    boxSizing: "border-box",
    py: "12px",
    borderBottom: "1px solid #E5E7EB",
  },

  additionalMilestoneItemLast: {
    borderBottom: "none",
  },

  additionalTitleRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
  },

  additionalMilestoneTitle: {
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 700,
    color: "custom.foreground.primary",
  },
  skippedText: {
    fontSize: "12px",
    fontWeight: "500",
    color: "#F26161",
  },

  additionalTimestamp: {
    
    fontSize: "14px",
    lineHeight: "17px",
    color: "custom.foreground.primary",
    width: "fit-content",
    border: "1px solid transparent",
    borderRadius: "4px",
  },
  dateStyle: {
    mt: "3px",
    fontSize: "12px",
    lineHeight: "17px",
    color: "custom.foreground.primary",
    width: "fit-content",
    border: "1px solid transparent",
    borderRadius: "4px",
  },

  additionalEditableTimestamp: {
    borderColor: "#79747E",
    cursor: "pointer",
    px: "3px",
  },

  additionalLoggedBy: {
    mt: "2px",
    fontSize: "11px",
    lineHeight: "15px",
    color: "custom.foreground.secondary",
  },

  additionalAlertBox: {
    width: "fit-content",
    maxWidth: "100%",
    boxSizing: "border-box",
    mt: "6px",
    border: "1px solid",
    borderRadius: "4px",
    px: "8px",
    py: "6px",
  },

  reasonBox: {
    width: "100%",
    boxSizing: "border-box",
    mt: "8px",
    border: "1px solid #E1E4EA",
    borderRadius: "8px",
    px: "10px",
    py: "8px",
    backgroundColor: "#F8F9FA",
  },

  reasonLabel: {
    fontSize: "11px",
    lineHeight: "15px",
    color: "#9299AA",
    mb: "2px",
  },

  reasonText: {
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 400,
    color: "custom.foreground.primary",
  },
};

export default MilestoneTimeline;
